import { NetworkConfiguration } from "../../network/networkConfriguation";
import { callAPI } from "../../network/networkConnection";

const API = process.env.REACT_APP_APIUrl;

/**
 * Upload a single Excel file to the backend for import.
 * @param {File} file - The Excel file to upload.
 * @param {string} source - Label for the file source, e.g. "Yeky" or "Sunny".
 * @param {{ orderErrorRowDelta?: number, postageErrorRowDelta?: number }} [rowMeta] - When importing a browser-built chunk, maps chunk row numbers back to the original workbook for error messages.
 */
const importExcelFile = (file, source, rowMeta = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("source", source);
  const orderD = Number(rowMeta.orderErrorRowDelta);
  const postageD = Number(rowMeta.postageErrorRowDelta);
  if (Number.isFinite(orderD) && orderD !== 0) {
    formData.append("orderErrorRowDelta", String(Math.trunc(orderD)));
  }
  if (Number.isFinite(postageD) && postageD !== 0) {
    formData.append("postageErrorRowDelta", String(Math.trunc(postageD)));
  }

  return callAPI(API + NetworkConfiguration.CHINA_UPDATE_IMPORT, "POST", {}, formData);
};

/**
 * Fetch Order Details records with pagination, search, and IST date range.
 */
const orderDetailsListParams = ({
  page = 1,
  limit = 20,
  search = "",
  startDate = "",
  endDate = "",
  indiaSNoType = "",
  indiaSNoCustomPrefix = "",
  source = "",
  status = "",
} = {}) =>
  new URLSearchParams({
    page, limit,
    ...(search               && { search }),
    ...(startDate            && { startDate }),
    ...(endDate              && { endDate }),
    ...(indiaSNoType         && { indiaSNoType }),
    ...(indiaSNoCustomPrefix && { indiaSNoCustomPrefix }),
    ...(source               && { source }),
    ...(status               && { status }),
  });

const getOrderDetails = (opts = {}) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_CHINA_ORDER_DETAILS}?${orderDetailsListParams(opts)}`,
    "GET",
    { "Content-Type": "application/json" },
    null
  );

/** Same data as Order Details; uses `in-comments` role permission on the API. */
const getInCommentsOrderDetails = (opts = {}) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_CHINA_IN_COMMENTS}?${orderDetailsListParams(opts)}`,
    "GET",
    { "Content-Type": "application/json" },
    null
  );

/**
 * Fetch Postage to India records with pagination, search, and IST date range.
 */
const getPostageToIndia = ({ page = 1, limit = 20, search = "", startDate = "", endDate = "", source = "" } = {}) => {
  const params = new URLSearchParams({
    page, limit,
    ...(search    && { search }),
    ...(startDate && { startDate }),
    ...(endDate   && { endDate }),
    ...(source    && { source }),
  });
  return callAPI(
    `${API}${NetworkConfiguration.GET_POSTAGE_TO_INDIA}?${params}`,
    "GET",
    { "Content-Type": "application/json" },
    null
  );
};


const updateOrderDetailsRecord = (id, data) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_CHINA_ORDER_DETAILS}/${id}`,
    "PUT",
    { "Content-Type": "application/json" },
    JSON.stringify(data)
  );

const updateInCommentsOrderRecord = (id, data) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_CHINA_IN_COMMENTS}/${id}`,
    "PUT",
    { "Content-Type": "application/json" },
    JSON.stringify(data)
  );

const deleteOrderDetailsRecord = (id, password) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_CHINA_ORDER_DETAILS}/${id}`,
    "DELETE",
    { "Content-Type": "application/json" },
    JSON.stringify({ password })
  );

const deleteAllOrderDetails = (password) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_CHINA_ORDER_DETAILS}/all`,
    "DELETE",
    { "Content-Type": "application/json" },
    JSON.stringify({ password })
  );

const updatePostageRecord = (id, data) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_POSTAGE_TO_INDIA}/${id}`,
    "PUT",
    { "Content-Type": "application/json" },
    JSON.stringify(data)
  );

const deletePostageRecord = (id, password) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_POSTAGE_TO_INDIA}/${id}`,
    "DELETE",
    { "Content-Type": "application/json" },
    JSON.stringify({ password })
  );

const deleteAllPostageToIndia = (password) =>
  callAPI(
    `${API}${NetworkConfiguration.GET_POSTAGE_TO_INDIA}/all`,
    "DELETE",
    { "Content-Type": "application/json" },
    JSON.stringify({ password })
  );

/**
 * Sheet sync poll: use query cache-bust only. Do NOT set Cache-Control/Pragma on GET —
 * those trigger a CORS preflight whose Access-Control-Request-Headers must be allowed by
 * the API; ours only whitelists a fixed set (see backend app.js cors.allowedHeaders).
 */
const getSheetSyncJob = (jobId) => {
  const bust = `_cb=${Date.now()}`;
  const base = `${API}${NetworkConfiguration.GET_CHINA_SHEET_SYNC_JOBS}/${encodeURIComponent(jobId)}`;
  const sep = base.includes("?") ? "&" : "?";
  return callAPI(
    `${base}${sep}${bust}`,
    "GET",
    { "Content-Type": "application/json" },
    null
  );
};

const listSheetSyncJobs = ({ limit = 10 } = {}) => {
  const q = new URLSearchParams({
    limit: String(limit),
    _cb: String(Date.now()),
  });
  return callAPI(
    `${API}${NetworkConfiguration.GET_CHINA_SHEET_SYNC_JOBS}?${q}`,
    "GET",
    { "Content-Type": "application/json" },
    null
  );
};

/** Queue sync of all Order Details (by China S.No.) to the live Google Sheet. */
const syncAllOrderDetailsToLiveSheet = () =>
  callAPI(
    `${API}${NetworkConfiguration.CHINA_ORDER_DETAILS_SYNC_LIVE_SHEET}`,
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({})
  );

/** Cancel pending/processing sheet sync jobs (stops upsert between DB chunks). */
const cancelSheetSyncJobs = (jobIds) =>
  callAPI(
    `${API}${NetworkConfiguration.CHINA_SHEET_SYNC_JOBS_CANCEL}`,
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ jobIds: Array.isArray(jobIds) ? jobIds : [] })
  );

export const chinaOrderImportService = {
  importExcelFile,
  getOrderDetails,
  getInCommentsOrderDetails,
  getPostageToIndia,
  updateOrderDetailsRecord,
  updateInCommentsOrderRecord,
  deleteOrderDetailsRecord,
  deleteAllOrderDetails,
  updatePostageRecord,
  deletePostageRecord,
  deleteAllPostageToIndia,
  getSheetSyncJob,
  listSheetSyncJobs,
  syncAllOrderDetailsToLiveSheet,
  cancelSheetSyncJobs,
};
